'use client';

import { format, isValid, parse } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/** Display and store dates as MM/dd/yyyy (e.g. 02/14/2025). */
const DATE_FORMAT = 'MM/dd/yyyy';

export interface DatePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  id?: string;
  required?: boolean;
  className?: string;
  /** If true, show only month/year (e.g. MM/yyyy). Used for "Placed in service" type fields. */
  monthYearOnly?: boolean;
}

const MONTH_YEAR_FORMAT = 'MM/yyyy';

/** Format as 2 digits, clamp to 1–max. */
function clampSegment(val: number, max: number): string {
  const n = Math.min(Math.max(val || 0, 1), max);
  return String(n).padStart(2, '0');
}

/**
 * Format raw digits with auto slashes. Month 01–12, day 01–31.
 * If user types e.g. "31", treat as month 03 and day 1 ("03/1") so month is never > 12.
 */
function formatWithSlashes(digits: string, monthYearOnly: boolean): string {
  if (monthYearOnly) {
    const d = digits.slice(0, 6);
    if (d.length <= 2) {
      if (d.length === 2) return clampSegment(parseInt(d, 10), 12);
      return d;
    }
    const month = clampSegment(parseInt(d.slice(0, 2), 10), 12);
    return month + '/' + d.slice(2);
  }
  const d = digits.slice(0, 8);
  if (d.length <= 2) {
    if (d.length === 2) {
      const n = parseInt(d, 10);
      if (n > 12) return clampSegment(Math.floor(n / 10), 12) + '/' + (n % 10);
      return clampSegment(n, 12);
    }
    return d;
  }
  if (d.length <= 4) {
    const month = clampSegment(parseInt(d.slice(0, 2), 10), 12);
    const dayPart = d.slice(2);
    if (dayPart.length === 2)
      return month + '/' + clampSegment(parseInt(dayPart, 10), 31);
    return month + '/' + dayPart;
  }
  const month = clampSegment(parseInt(d.slice(0, 2), 10), 12);
  const day = clampSegment(parseInt(d.slice(2, 4), 10), 31);
  return month + '/' + day + '/' + d.slice(4);
}

/** Inline date input that still opens the calendar popover. User can type the date (slashes auto-filled) or pick from the calendar. */
export function DatePickerInput({
  value,
  onChange,
  placeholder = 'MM/DD/YYYY',
  label,
  id,
  required,
  className,
  fullWidth,
  monthYearOnly = false,
}: DatePickerProps & { fullWidth?: boolean }) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  const dateValue = React.useMemo(() => {
    const str = value ?? inputValue;
    if (!str || typeof str !== 'string') return undefined;
    const parsed = monthYearOnly
      ? parse(str, MONTH_YEAR_FORMAT, new Date())
      : parse(str, DATE_FORMAT, new Date());
    return isValid(parsed) ? parsed : undefined;
  }, [value, inputValue, monthYearOnly]);

  // Sync input from controlled value when it changes from parent (e.g. form reset)
  React.useEffect(() => {
    if (value !== undefined && value !== null) {
      setInputValue(value);
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    const formatted = formatWithSlashes(raw, monthYearOnly);
    setInputValue(formatted);
    // Always notify parent so controlled value updates and input displays correctly
    onChange?.(formatted === '' ? '' : formatted);
  };

  const handleInputBlur = () => {
    if (inputValue && !dateValue) {
      // Invalid partial input: clear or leave as-is; optionally clear on blur
      const parsed = monthYearOnly
        ? parse(inputValue, MONTH_YEAR_FORMAT, new Date())
        : parse(inputValue, DATE_FORMAT, new Date());
      if (!isValid(parsed)) onChange?.('');
    }
  };

  const handleSelect = (d: Date | undefined) => {
    if (!d) {
      setInputValue('');
      onChange?.('');
      return;
    }
    const str = monthYearOnly
      ? format(d, MONTH_YEAR_FORMAT)
      : format(d, DATE_FORMAT);
    setInputValue(str);
    onChange?.(str);
    setOpen(false);
  };

  const displayValue = value ?? inputValue;

  return (
    <div className={cn('space-y-2', fullWidth && 'sm:col-span-2', className)}>
      {label && (
        <label
          htmlFor={id}
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {label}
        </label>
      )}
      <div className="relative flex w-full items-center">
        <Input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={monthYearOnly ? 'MM/YYYY' : placeholder}
          value={displayValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={(e) => e.key === '/' && e.stopPropagation()}
          className={cn(
            'pr-10',
            !displayValue && 'text-muted-foreground'
          )}
          required={required}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="absolute right-0 top-0 h-full rounded-l-none border-l-0 px-3"
              aria-label="Pick a date"
            >
              <CalendarIcon className="h-4 w-4 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={dateValue}
              onSelect={handleSelect}
              defaultMonth={dateValue ?? undefined}
              captionLayout="dropdown"
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
